import { useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { inviteMessage } from '../InviteFriend/InviteFriend';
import css from './InviteStrip.module.css';
import { InviteBonusLine, useGrowthStatus } from '../GrowthPromos/GrowthPromos';
import ListenerCopyPosts from '../ListenerCopyPosts/ListenerCopyPosts';

/**
 * "Invite a friend" card above the footer on every page (Sam, Oct 15).
 * Signed in: personal ?ref= link (same flow as the dashboard card). Reward wording only while the
 * server referral switch is ON. Signed out: share the site + sign up. The site sends nothing.
 */
const InviteStripInner = () => {
  const me = useSelector(state => state?.user?.currentUser);
  const id = me?.id?.uuid;
  const [copied, setCopied] = useState(false);
  const [reward, setReward] = useState(null);
  const [canShare, setCanShare] = useState(false);
  const growth = useGrowthStatus();
  const listenerBonus = !!(id && growth?.talkerBonus && growth?.listener);
  useEffect(() => {
    setCanShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);
  useEffect(() => {
    if (!id || typeof fetch !== 'function') return undefined;
    let live = true;
    fetch('/api/referral/status', { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null))
      .then(j => live && j && setReward(j))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [id]);
  const roleNow = (me || {})?.attributes?.profile?.publicData?.accountRole;
  const isListenerRole = roleNow === 'listen' || roleNow === 'both';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://sweetlisteners.com';
  const link = id ? `${origin}/?ref=${id}` : `${origin}/`;
  const msg = id
    ? inviteMessage(link)
    : `Thought you'd like this: friendly one-on-one conversations with kind listeners. ${link}`;
  const copy = () => {
    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    };
    try {
      navigator.clipboard.writeText(link).then(done, done);
    } catch (e) {
      done();
    }
  };
  const share = () => navigator.share({ title: 'Sweet Skillz', text: msg }).catch(() => {});
  return (
    <aside className={css.root} data-invite-strip={id ? 'member' : 'guest'}>
      <div className={css.inner}>
        <div className={css.copy}>
          <strong className={css.title}>Invite a friend</strong>
          <span className={css.body}>
            {listenerBonus ? null : id
              ? reward?.enabled
                ? 'Give a Sweet Treat, get a Sweet Treat. Your friend gets $10 off their first chat when they sign up (a free 15-minute text chat). When they finish their first conversation, you get one too. Use it within 30 days.'
                : 'Know someone who’d enjoy a good conversation? Share your personal link.'
              : 'Sign up to get your invite link.'}
          </span>
          {listenerBonus ? <InviteBonusLine tone="dark" /> : null}
          {id ? (
            <input
              className={css.linkBox}
              readOnly
              value={link}
              aria-label="Your personal invite link"
              onFocus={e => e.target.select()}
              data-invite-strip-link="true"
            />
          ) : null}
        </div>
        <div className={css.actions}>
          {canShare ? (
            <button type="button" className={css.primary} onClick={share} data-invite-strip-share="true">
              Share
            </button>
          ) : null}
          {id ? (
            <button type="button" className={canShare ? css.btn : css.primary} onClick={copy} data-invite-strip-copy="true">
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          ) : null}
          {id ? null : (
            <a className={css.primary} href="/signup" data-invite-strip-signup="true">
              Sign up free
            </a>
          )}
        </div>
      </div>
      {id && isListenerRole ? (
        <div className={css.inner}>
          <ListenerCopyPosts link={link} tone="dark" />
        </div>
      ) : null}
    </aside>
  );
};

// Sam 7:51 PM Oct 23: Invite a friend appears ONLY on Dashboard and Browse, for signed-in people.
// Dashboard: skip when the dashboard already renders its own Invite Friends section.
const InviteStrip = () => {
  const { pathname } = useLocation();
  const signedIn = useSelector(state => !!state?.user?.currentUser?.id);
  const [dashHasOwn, setDashHasOwn] = React.useState(true);
  const onDash = /^\/dashboard/.test(pathname || '');
  React.useEffect(() => {
    if (!onDash || typeof document === 'undefined') return undefined;
    const check = () => setDashHasOwn(!!document.querySelector('[data-dashboard-invite]'));
    check();
    const t = window.setTimeout(check, 1500);
    return () => window.clearTimeout(t);
  }, [onDash, pathname]);
  if (!signedIn) return null;
  const onBrowse = /^\/(s|skills\/browse)(\/|$)/.test(pathname || '');
  if (onBrowse) return <InviteStripInner />;
  if (onDash && !dashHasOwn) return <InviteStripInner />;
  return null;
};

export default InviteStrip;
